import React, { useEffect, useState } from "react";
import styles from './CreateStudent.module.scss';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import StudentForm from "../../../components/studentForm/StudentForm";
import axiosClient from "../../../api/axiosClient";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import { formatPhoneNumber } from "../../../utils/stringUtils";
import { Tab, TabPanel, Tabs } from "../../../components/tabs/Tabs";
import Icon from "../../../components/icon/Icon";
import { format } from "date-fns";

const CreateStudent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const initialFormData = {    
    profilePicture: null,
    firstName: '',
    lastName: '',
    email: '',
    dob: '',
    mobileNumber: '',
    specialityIds: [],
    studentId: '',
    year:'',
    semester:'',
    month:'',
    enrollmentDate:'',
    graduationDate:'',
    instructorIds: [],
  };
  const [formData, setFormData] = useState(initialFormData);
  const [specialityList, setSpecialityList] = useState([]);
  const [yearData, setYearData] = useState([]);
  const [instructorList, setInstructorList] = useState([]);
  const { studentId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      axiosClient.get('specialities')
      .then ((specialityResponse) => {
        setSpecialityList(specialityResponse.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
      })
      await axiosClient.get(`years/details`)
      .then((response) => {
        const years = [];
        response.data.forEach(item => {
            years.push({
              id: item.year.id,
              year: item.year.year,
              semesters: item.semesters
            });
        })
        setYearData(years);
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
    }
    fetchData();
  }, [showToast]);

useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`users/instructors`)
      .then((response) => {
        if(response.data?.instructors) {
          setInstructorList(response.data.instructors.map((item) => ({
            value: item.id,
            label: `${item.firstName} ${item.lastName}`
          })));
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
    };
    fetchData();
  }, [showToast]);


  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`users/student-management/students/details/${studentId}`)
      .then((response) => {
        setFormData({                     
          profilePicture: response?.data?.profilePicture,
          firstName: response?.data?.firstName,
          lastName: response?.data?.lastName,
          email: response?.data?.email,
          dob: response?.data?.dateOfBirth ? format(response.data.dateOfBirth, 'MM/dd/yyyy') : '', 
          mobileNumber: response?.data?.mobileNumber ? formatPhoneNumber(response.data.mobileNumber) : '',
          studentId: response?.data?.studentId,
          year: response?.data?.year?.id,
          semester: response?.data?.semester?.id,
          month: response?.data?.month?.id,
          enrollmentDate: response?.data?.rotationStartDate ? format(response.data.rotationStartDate, 'MM/dd/yyyy') : '', 
          graduationDate: response?.data?.rotationEndDate ? format(response.data.rotationEndDate, 'MM/dd/yyyy') : '',  
          specialityIds: response.data?.specialities ? response.data.specialities.map(item => item.id) : [],
          instructorIds: response.data?.allocations ? response.data.allocations.map(item=>item.instructor.id) : [],   
        })
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    }

    if(studentId) {
      fetchData()
    }
  }, [studentId, showToast]);

  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleSave = async (imageFile) => {
    const data = {
      studentId:formData.studentId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobileNumber: formData.mobileNumber.replace(/\D/g, ''),
      dateOfBirth: format(formData.dob,"yyyy-MM-dd"),  
      rotationStartDate: format(formData.enrollmentDate,"yyyy-MM-dd"),
      rotationEndDate: format(formData.graduationDate,"yyyy-MM-dd"),
      yearId:formData.year,
      monthId:formData.month,
      semesterId:formData.semester,  
      specialities: formData.specialityIds ?? [],   
    }

    const updatedFormData = new FormData();
    for (let key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (Array.isArray(data[key])) {
          data[key].forEach(item => {
            if (item != null) { // Check for null or undefined
              updatedFormData.append(`${key}[]`, item);
            }
          });
        } else {
          if (data[key] != null) { // Check for null or undefined
            updatedFormData.append(key, data[key]);
          }
        }
      }
    }
    if(imageFile) {
      updatedFormData.append('profilePicture', imageFile);
    }

    setIsLoading(true);
    if(studentId) {
      await axiosClient.patch(`users/student-management/students/details/${studentId}`, updatedFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })
      .then((response) => {
        showToast(response.message);
        navigate('/students');
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        scrollToTop();
      });
    } 
    else {   
      await axiosClient.post(`students/create`, updatedFormData, 
        {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }
    )
      .then((response) => {
        showToast(response.message);
        navigate('/students');
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        scrollToTop();
      });
    }
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Student Management', path: '/students' },
            { label: studentId ? `Edit Student` : `Create Student`}
          ]}
        />
      </div>
       <Tabs>
         <Tab>
            <Icon name="user" />
              <span>About</span>
         </Tab>
         <Tab>
            <Icon name="clock" />
              <span>Schedule</span>
            </Tab>
         <Tab>
            <Icon name="clipboard" />
              <span>Documents</span>
         </Tab>
         <TabPanel>
          <div className={styles.container}>
            <StudentForm 
            formData={formData}
            specialityList={specialityList}
            yearData={yearData}
            instructorList={instructorList}
            onDataChange={(updatedData) => {
            setFormData((prevData) => {
              return {
                ...prevData,
                ...updatedData
              }
            });
            }}
            onSubmit={(imageFile) => handleSave(imageFile)}
            />
          </div>
         </TabPanel>
        </Tabs>
    </>
  );
};

export default CreateStudent;